import {
  HealthSystemDALEnumsAnimalTrendDataType,
  HealthSystemDomainAnimalsCommandsSearchCompareAnimalsTrendDataQuery,
  HealthSystemDomainAnimalsResultAnimalsFarmSearchRequest,
  HealthSystemDomainAnimalsResultAnimalTrendDataViewModel
} from '../api/HealthSystemApi';
import {
  CreateScheduledReportInput,
  ReportSourceViewModel,
  ScheduledReportViewModel,
  UpdateScheduledReportInput
} from '../models/report';
import { RelativeDateWindow, resolveRelativeDateWindow } from '../utils/date-range';
import ApiService from './ApiService';
import BaseService from './BaseService';

/**
 * Scheduled reports CRUD backed by the real /scheduled-reports API (AB#3605).
 * previewAsync resolves the relative window to real dates and calls the existing
 * compare trend endpoint directly, reusing the same data the compare feature shows.
 */
export class ReportService extends BaseService {
  private apiService = new ApiService();

  getAllAsync = async (): Promise<ScheduledReportViewModel[]> => {
    const res = await this.Fetch('/scheduled-reports', 'GET');
    return this.FromResult<ScheduledReportViewModel[]>(res);
  };

  createAsync = async (
    input: CreateScheduledReportInput,
    sources: ReportSourceViewModel[]
  ): Promise<ScheduledReportViewModel> => {
    const body = {
      name: input.name ?? null,
      animalTrendDataType: input.animalTrendDataType,
      relativeDateWindow: input.relativeDateWindow,
      frequency: input.frequency,
      dayOfWeek: input.dayOfWeek,
      recipients: input.recipients,
      sources: sources.map((source) => ({
        savedSearchId: source.savedSearchId,
        name: source.name,
        query: source.query
      }))
    };

    const res = await this.Fetch('/scheduled-reports', 'POST', JSON.stringify(body));
    return this.FromResult<ScheduledReportViewModel>(res);
  };

  updateAsync = async (
    id: string,
    input: UpdateScheduledReportInput,
    sources: ReportSourceViewModel[]
  ): Promise<ScheduledReportViewModel> => {
    const body = {
      name: input.name ?? null,
      animalTrendDataType: input.animalTrendDataType,
      relativeDateWindow: input.relativeDateWindow,
      frequency: input.frequency,
      dayOfWeek: input.dayOfWeek,
      recipients: input.recipients,
      sources: sources.map((source) => ({
        savedSearchId: source.savedSearchId,
        name: source.name,
        query: source.query
      }))
    };

    const res = await this.Fetch(`/scheduled-reports/${id}`, 'PUT', JSON.stringify(body));
    return this.FromResult<ScheduledReportViewModel>(res);
  };

  setActiveAsync = async (id: string, isActive: boolean): Promise<void> => {
    await this.Fetch(`/scheduled-reports/${id}/active`, 'PUT', JSON.stringify({ isActive }));
  };

  deleteAsync = async (id: string): Promise<void> => {
    await this.Fetch(`/scheduled-reports/${id}`, 'DELETE');
  };

  sendNowAsync = async (id: string): Promise<{ sentAt: string }> => {
    const res = await this.Fetch(`/scheduled-reports/${id}/send-now`, 'POST');
    return this.FromResult<{ sentAt: string }>(res);
  };

  /**
   * Resolves the relative window to real dates and fetches real trend data from the
   * existing compare endpoint - lets the preview reuse the exact same data source.
   */
  previewAsync = async (
    animalTrendDataType: HealthSystemDALEnumsAnimalTrendDataType,
    relativeDateWindow: RelativeDateWindow,
    sources: ReportSourceViewModel[]
  ): Promise<Record<string, HealthSystemDomainAnimalsResultAnimalTrendDataViewModel>> => {
    const { from, to } = resolveRelativeDateWindow(relativeDateWindow);

    const queries = sources.reduce<Record<string, HealthSystemDomainAnimalsResultAnimalsFarmSearchRequest>>(
      (acc, source, index) => {
        acc[index.toString()] = {
          ...source.query,
          dateFrom: from.toISOString(),
          dateTo: to.toISOString()
        };
        return acc;
      },
      {}
    );

    const command: HealthSystemDomainAnimalsCommandsSearchCompareAnimalsTrendDataQuery = {
      animalTrendDataType,
      queries
    };

    const result = await this.apiService.api.animals.animalsGetCompareSearchAnimalsTrendData(command);
    return result.data;
  };
}

export default ReportService;
